// Plan-catalog helpers (2026-10-09). The catalog comes from the backend
// (GET /v1/public/billing/catalog), which reads the `tier`, `feature_module` and `entitlement`
// tables — the ONLY place plans are defined. Nothing here knows any plan by name: order comes
// from `rank`, "recommended" and "on sale" are flags on the row, and features are module ids.
// To add a plan or move a feature, change the database (see backend docs/billing-plans.md).

/** "$1,799" for 179900; cents kept when there are any ("$12.50"). */
export const formatPrice = (cents) => {
  if (cents === null || cents === undefined) return ''
  const whole = cents % 100 === 0
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 })}`
}

/** Every plan, smallest first — retired ones included (shops may still be on them). */
export const sortedTiers = (catalog) => [...(catalog?.tiers ?? [])].sort((a, b) => a.rank - b.rank)

/** Plans offered for sale, smallest first — the Free Trial package included (the sign-up picker). */
export const onSaleTiers = (catalog) => sortedTiers(catalog).filter((t) => t.onSale)

/**
 * The Free Trial package (`kind: 'TRIAL'`): a one-time purchase that lasts `durationDays`. It is
 * only ever bought first — never an upgrade, a "plan below", an upsell or a plan to compare.
 */
export const isTrialPackage = (tier) => tier?.kind === 'TRIAL'

/** Monthly plans on sale, smallest first: what upgrades, upsells and the Billing comparison use. Options included. */
export const monthlyTiers = (catalog) => onSaleTiers(catalog).filter((t) => !isTrialPackage(t))

/**
 * An option (`optionOf` set) is a plan that builds on a parent plan ("AI Complete Assist" on AI Complete).
 * It gets a card of its own, right after its parent's, reading "Everything in <parent>, plus:".
 */
export const isOption = (tier) => Boolean(tier?.optionOf)

/** The plans that get a card, one each, side by side: every monthly plan on sale, options included. */
export const cardTiers = (catalog) => monthlyTiers(catalog)

/** "/ location / mo" — or "/ location / yr", "/ location / 3 mo" for other intervals. */
export const priceSuffix = (tier) => {
  if (isTrialPackage(tier)) return `one-time · ${tier.durationDays} days`
  const unit = tier?.interval === 'year' ? 'yr' : 'mo'
  const count = tier?.intervalCount ?? 1
  return `/ location / ${count > 1 ? `${count} ` : ''}${unit}`
}

/** The price a new subscriber pays after the package discount (the card's headline when one is running). */
export const effectivePriceCents = (tier) => tier?.discount?.discountedPriceCents ?? tier?.priceCents

/** "10% off for 3 months" / "$50 off forever" — the running package discount, or null. */
export const discountLine = (tier) => {
  const d = tier?.discount
  if (!d) return null
  const what = d.type === 'PERCENT' ? `${Number(d.percentOff)}% off` : `${formatPrice(d.amountOffCents)} off`
  const span = d.durationMonths == null ? 'forever' : d.durationMonths === 1 ? 'for the first month' : `for ${d.durationMonths} months`
  return `${what} ${span}`
}

/** "$1 for the first 30 days" — the plan's intro period, or null when it has none. */
export const introLine = (tier) => {
  if (!tier || !(tier.introDays > 0)) return null
  const fee = tier.introFeeCents > 0 ? formatPrice(tier.introFeeCents) : 'Free'
  return `${fee} for the first ${tier.introDays} days`
}

/**
 * How many intro periods one card and one login may each start — the backend's billing.intro.max-per-buyer
 * (CAR-4014), sent on the catalog. Null when the backend doesn't say, so the copy never guesses a number.
 */
export const introMaxPerBuyer = (catalog) => (catalog?.introMaxPerBuyer > 0 ? catalog.introMaxPerBuyer : null)

/** "limited to 10 locations per card and per login"; without a cap from the backend, just "limited per card and per login". */
const introLimitPhrase = (max) => {
  if (!(max > 0)) return 'limited per card and per login'
  return `limited to ${max === 1 ? 'one location' : `${max} locations`} per card and per login`
}

/** The intro offer's per-card / per-login limit. Null when the plan has no paid intro period. */
export const introLimitLine = (tier, max) => (tier?.introDays > 0 ? introLimitNotice(tier.introFeeCents, max) : null)

/** The same notice from an intro fee alone (the sign-up wizard reads both from the registration config). */
export const introLimitNotice = (feeCents, max) =>
  feeCents > 0 ? `The ${formatPrice(feeCents)} intro offer is ${introLimitPhrase(max)}.` : null

/** The card's limit lines ("Up to 50 ROs / month"), from the catalog; empty when the plan has none to show. */
export const limitLines = (tier) => (tier?.limits ?? []).map((l) => l.display).filter(Boolean)

/** A plan's value for a limit key: a number, or null = unlimited / not set. */
export const limitValue = (tier, key) => (tier?.limits ?? []).find((l) => l.key === key)?.value ?? null

/** The Free Trial package when it is on offer (on sale, priced, trials not frozen). */
export const trialPackage = (catalog) => onSaleTiers(catalog).find(isTrialPackage)

export const tierById = (catalog, id) => (catalog?.tiers ?? []).find((t) => t.id === id)

/** True when `toId` is a bigger plan than `fromId`. Unknown plans → false. */
export const isUpgrade = (catalog, fromId, toId) => {
  const from = tierById(catalog, fromId)
  const to = tierById(catalog, toId)
  return Boolean(from && to && !isTrialPackage(to) && to.rank > from.rank)
}

/** The next bigger plan on sale, or undefined at the top. */
export const nextTierUp = (catalog, id) => {
  const current = tierById(catalog, id)
  return current ? monthlyTiers(catalog).find((t) => t.rank > current.rank) : undefined
}

/** The next smaller plan on sale, or undefined at the bottom. */
export const nextTierDown = (catalog, id) => {
  const current = tierById(catalog, id)
  return current
    ? monthlyTiers(catalog)
        .filter((t) => t.rank < current.rank)
        .pop()
    : undefined
}

/** The smallest plan that includes a feature — preferring plans on sale. */
export const requiredTierFor = (catalog, moduleId) =>
  monthlyTiers(catalog).find((t) => t.modules.includes(moduleId)) ??
  sortedTiers(catalog).find((t) => !isTrialPackage(t) && t.modules.includes(moduleId))

/** The plan offered as the default choice: the one flagged recommended, else the biggest card plan on sale. */
export const recommendedTier = (catalog) => {
  const tiers = cardTiers(catalog)
  return tiers.find((t) => t.recommended) ?? tiers[tiers.length - 1]
}

/**
 * The features section of a plan card: the plan's own heading (`featuresHeading`, e.g. "Everything in AI Core,
 * plus") and feature lines (`highlights`), exactly as configured in Plans & Features. Nothing is derived, merged
 * or filtered here; no heading configured = none shown.
 */
export const cardFeatures = (tier) => ({
  heading: String(tier?.featuresHeading ?? '').trim() || null,
  items: tier?.highlights ?? []
})

/**
 * Plans to show side by side on Billing: the monthly plans on sale, plus the current plan when it
 * isn't one of them (retired, or the Free Trial the location is on).
 */
export const tiersToCompare = (catalog, currentTierId) => {
  const current = tierById(catalog, currentTierId)
  const cards = cardTiers(catalog)
  if (current && !cards.some((t) => t.id === current.id)) {
    return sortedTiers(catalog).filter((t) => t.id === current.id || cards.some((m) => m.id === t.id))
  }
  return cards
}
